<template>
  <div class="search-bar mb-2 px-2 flex">
    <div class="flex flex-col sm:flex-row w-full justify-between">
      <!-- 左側區域 - 描述和提示 -->
      <div class="flex flex-col items-start sm:mt-4 lg:mt-0">
        <div>{{ $t('profiles.analytics_search_form.models_summary') }}</div>
        <div class="flex items-center text-[#B0AEAE] text-12px inline-block">
          {{ $t('profiles.analytics_search_form.with_previous_period') }}
          
          <!-- 提示組件 -->
          <nav-panel :panel-class="'w-[288px]'" class="ml-2">
            <template #icon>
              <img 
                src="../../images/info-white.svg" 
                class="w-6 h-6 opacity-50" 
                alt="info-white" 
              />
            </template>
            <template #content>
              <div class="custom-content text-[12px] text-center">
                <p>{{ $t('profiles.analytics_search_form.comparison_with_previous_period') }}</p>
                <span class="text-orange-10 font-bold">
                  <span>{{ formatPreviousStartDate }}</span>
                  <span> to </span>
                  <span>{{ formatPreviousEndDate }}</span>
                </span>
              </div>
            </template>
          </nav-panel>
        </div>
      </div>

      <!-- 右側區域 - 日期範圍選擇器 -->
      <div class="flex items-center">
          <date-range-picker
            v-model="dateRange"
            :placeholder="$t('profiles.analytics_search_form.select_date_range')"
            ref="calendarRef"
            calendar-id="analytics-calendar"
            @visibility-change="isCalendarVisible = $event"
          />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { format, parse, subDays } from 'date-fns'
import debounce from 'lodash/debounce'
import NavPanel from './NavPanel.vue'
import DateRangePicker from './DateRangePicker.vue'

export default {
  name: 'ProfileAnalyticsSearchForm',
  components: {
    NavPanel,
    DateRangePicker
  },
  setup(props) {
    // 從父組件注入 analytics 實例
    const analytics = inject('analytics')
    
    if (!analytics) {
      console.error('ProfileAnalyticsSearchForm: analytics not provided')
      return {}
    }
    
    const {
      chartParams,
      loading,
      updateDateRange,
      fetchAnalyticsData,
      formatDateTip
    } = analytics

    // 本地狀態
    const calendarRef = ref(null)
    const isCalendarVisible = ref(false)
    const scrollListener = ref(null)

    // 日期範圍對象，用於與 DateRangePicker 交互
    const dateRange = computed({
      get: () => ({
        start: chartParams.start ? chartParams.start.replace(/\//g, '-') : '',
        end: chartParams.end ? chartParams.end.replace(/\//g, '-') : ''
      }),
      set: (value) => {
        if (value.start && value.end) {
          const newStart = format(parse(value.start, 'yyyy-MM-dd', new Date()), 'yyyy/MM/dd')
          const newEnd = format(parse(value.end, 'yyyy-MM-dd', new Date()), 'yyyy/MM/dd')
          
          // 檢查日期是否真的改變了
          const currentStart = chartParams.start
          const currentEnd = chartParams.end
          
          if (newStart !== currentStart || newEnd !== currentEnd) {
            updateDateRange({
              start: newStart,
              end: newEnd
            })
            
            // 只在日期真正改變時才執行搜索
            nextTick(() => {
              fetchAnalyticsData()
            })
          }
        }
      }
    })

    // 前一期間日期格式化
    const formatPreviousStartDate = computed(() => {
      const dateTip = formatDateTip()
      return dateTip.previousStart
    })

    const formatPreviousEndDate = computed(() => {
      const dateTip = formatDateTip()
      return dateTip.previousEnd
    })

    // 滾動監聽處理
    const handleScroll = () => {
      if (calendarRef.value && isCalendarVisible.value) {
        calendarRef.value.hide()
      }
    }

    const debouncedHandleScroll = debounce(handleScroll, 100)

    // 添加滾動監聽
    const addScrollListener = () => {
      removeScrollListener()
      scrollListener.value = debouncedHandleScroll
      window.addEventListener('scroll', scrollListener.value, { passive: true })
    }

    // 移除滾動監聽
    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value)
        scrollListener.value = null
      }
    }

    // 生命週期鉤子
    onMounted(() => {
      addScrollListener()
    })

    onUnmounted(() => {
      removeScrollListener()
    })

    return {
      // 響應式數據
      dateRange,
      loading,
      calendarRef,
      isCalendarVisible,
      
      // 計算屬性
      formatPreviousStartDate,
      formatPreviousEndDate,
      
      // 事件處理
      fetchAnalyticsData
    }
  }
}
</script>
